import { useTranslation } from "react-i18next";

const STYLE = "mapbox/streets-v12";
const ZOOM = 14;
const WIDTH = 600;
const HEIGHT = 240;
// `22ff5f` matches --color-volt so the pin reads as VoltaRoute brand, not Mapbox default.
const PIN_COLOR = "22ff5f";

type Props = {
  lat: number;
  lng: number;
  stationName: string;
  navigateUrl: string;
};

export function StationMapPreview({ lat, lng, stationName, navigateUrl }: Props) {
  const { t } = useTranslation();
  const token = import.meta.env.VITE_MAPBOX_TOKEN as string | undefined;

  return (
    <section aria-labelledby="station-location-heading">
      <h2 id="station-location-heading" className="label-caps mb-2 text-fg-mute">
        {t("stations.location_heading")}
      </h2>
      {token ? (
        <a
          href={navigateUrl}
          className="block overflow-hidden rounded-lg border border-line focus:outline-none focus:ring-2 focus:ring-volt/40"
          data-testid="station-map-preview-link"
        >
          <img
            src={buildStaticMapUrl({ lat, lng, token })}
            alt={t("stations.location_alt", { name: stationName })}
            width={WIDTH}
            height={HEIGHT}
            loading="lazy"
            className="block aspect-[5/2] w-full bg-surface-sunk object-cover"
            data-testid="station-map-preview-img"
          />
        </a>
      ) : (
        <p
          className="rounded-lg border border-line bg-surface-sunk px-3 py-6 text-center text-xs text-fg-faint"
          data-testid="station-map-preview-fallback"
        >
          {t("stations.location_unavailable")}
        </p>
      )}
    </section>
  );
}

function buildStaticMapUrl({ lat, lng, token }: { lat: number; lng: number; token: string }): string {
  const pin = `pin-l+${PIN_COLOR}(${lng},${lat})`;
  const center = `${lng},${lat},${ZOOM}`;
  const size = `${WIDTH}x${HEIGHT}@2x`;
  return `https://api.mapbox.com/styles/v1/${STYLE}/static/${pin}/${center}/${size}?access_token=${encodeURIComponent(token)}`;
}
